/**
 * CFS product card behaviour (build spec §5).
 *
 * <cfs-product-card>: Add → − qty + stepper bound to the variant's cart line. Optimistic: the
 * number changes at once, taps are coalesced, one request sets the final quantity, and the card
 * rolls back and shows the cart's message (e.g. a MinMaxify limit) if the request fails.
 *
 * "Bought before" (T6.2, spec §14.3): for signed-in customers the layout renders the product ids
 * from their last 10 orders once (snippets/cfs-bought-before.liquid); matching cards get the badge,
 * keeping at most two badges (Offer/Multibuy > Pre-order > Bought before > New > Best seller).
 *
 * Multibuy: cards for products in an enabled Bundler bundle get the deal as a badge ("3 for £1.39"), from
 * window.bndlr.getBundles() (see loadMultibuys).
 *
 * Pre-orders: a card for a product that keeps selling at zero stock carries data-preorder and data-stock;
 * when the stepper goes past the stock on hand it shows "N on pre-order" (the Add button keeps saying Add).
 *
 * Products with more than one variant have a plain "Choose options" link to the product page, no JS.
 */
import { CART_STATE_EVENT, bindQuantityField, getCartState, quantityForVariant, setVariantTotal } from '@theme/cfs-cart';

const COALESCE_MS = 300;

/** @type {Set<string> | null} */
let boughtBefore = null;
function boughtBeforeIds() {
  if (!boughtBefore) {
    try {
      const el = document.getElementById('CfsBoughtBefore');
      boughtBefore = new Set(el ? JSON.parse(el.textContent ?? '[]').map(String) : []);
    } catch {
      boughtBefore = new Set();
    }
  }
  return boughtBefore;
}
const MESSAGE_MS = 6000;

/**
 * Multibuy badges from Bundler (owner, 8 Oct 2026). Bundler has no API for this, but its script
 * exposes window.bndlr.getBundles() (Bundler support): every bundle with its products, discount and
 * wording. Products in an enabled bundle get a badge with the deal ("3 for £1.39"). Only products
 * Bundler lists as active count; bundles limited to customer tags or outside their dates are left
 * out. The result is kept for the session, so cards on the next page show it straight away instead
 * of after Bundler's script has loaded; it is refreshed on every page.
 */
const MULTIBUY_KEY = 'cfs:multibuys';
const MULTIBUY_EVENT = 'cfs:multibuys';
const MULTIBUY_GROUPS_KEY = 'cfs:multibuy-groups'; // read by the basket's deal pills (assets/cfs-basket.js)
/** @type {Map<string, string>} product id → badge text */
let multibuys = new Map();
try {
  multibuys = new Map(JSON.parse(sessionStorage.getItem(MULTIBUY_KEY) ?? '[]'));
} catch {
  // private mode or bad data: wait for Bundler
}

/** £2, £1.39 */
const poundText = (/** @type {unknown} */ value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  return `£${Number.isInteger(n) ? n : n.toFixed(2)}`;
};

/** @param {any} bundle */
function multibuyLabel(bundle) {
  if (bundle.is_volume_bundle === 'true') {
    // e.g. "Buy 2 for £2.00"
    const match = /buy (?:any )?(\d+) for £\s?([\d.]+)/i.exec(`${bundle.description ?? ''} ${bundle.name ?? ''}`);
    return match && poundText(match[2]) ? `${match[1]} for ${poundText(match[2])}` : 'Multibuy';
  }
  const count = Number(bundle.minimum_requirements_num);
  if (bundle.discount_type === 'fixed_price' && bundle.minimum_requirements === 'n_products' && count > 1 && poundText(bundle.fixed_price_value)) {
    return `${count} for ${poundText(bundle.fixed_price_value)}`;
  }
  return 'Multibuy';
}

/** @param {any[]} bundles */
function buildMultibuys(bundles) {
  const map = new Map();
  /** @type {Map<string, string>} product id → bundle id: the basket counts a mix-and-match deal over all its products */
  const groups = new Map();
  const now = Date.now();
  for (const bundle of bundles) {
    if (bundle?.status !== 'enabled') continue;
    if (Array.isArray(bundle.limit_for_customer_tags) && bundle.limit_for_customer_tags.length) continue;
    if (bundle.use_date_condition === 'true') {
      const from = Date.parse(bundle.date_from ?? '');
      const to = Date.parse(bundle.date_to ?? '');
      if ((!Number.isNaN(from) && now < from) || (!Number.isNaN(to) && now > to)) continue;
    }
    const label = multibuyLabel(bundle);
    for (const product of Object.values(bundle.products ?? {})) {
      const id = String(/** @type {any} */ (product)?.id ?? '');
      if (id && /** @type {any} */ (product).status === 'active' && !map.has(id)) {
        map.set(id, label);
        groups.set(id, String(bundle.id));
      }
    }
  }
  try {
    sessionStorage.setItem(MULTIBUY_GROUPS_KEY, JSON.stringify([...groups]));
  } catch {
    // the basket's deal pills wait for the next page
  }
  document.dispatchEvent(new CustomEvent(MULTIBUY_GROUPS_KEY)); // the basket fills its deal pills
  return map;
}

/** Reads Bundler's bundles once its script is there (it loads after the theme), then updates the cards. */
function loadMultibuys(tries = 0) {
  const bndlr = /** @type {any} */ (window).bndlr;
  if (typeof bndlr?.getBundles === 'function') {
    let bundles;
    try {
      bundles = bndlr.getBundles();
    } catch {
      bundles = null;
    }
    const list = Array.isArray(bundles) ? bundles : Object.values(bundles ?? {});
    if (list.length) {
      const fresh = buildMultibuys(list);
      const text = JSON.stringify([...fresh]);
      try {
        sessionStorage.setItem(MULTIBUY_KEY, text);
      } catch {
        // not kept: the next page waits for Bundler again
      }
      if (text !== JSON.stringify([...multibuys])) {
        multibuys = fresh;
        document.dispatchEvent(new CustomEvent(MULTIBUY_EVENT));
      }
      return;
    }
  }
  if (tries < 40) window.setTimeout(() => loadMultibuys(tries + 1), 500);
}

/** Screen reader text for each badge type (the badges sit inside the aria-hidden image link). */
const BADGE_SR = { new: ', new', best: ', best seller', bought: ', bought before' };

/**
 * Max two badges: drops the lowest-priority ones (they come last) and their screen reader text.
 * @param {HTMLElement} card
 */
function trimBadges(card) {
  const badges = card.querySelector('[data-cfs-badges]');
  if (!badges) return;
  const all = badges.querySelectorAll('[data-cfs-badge]');
  for (let i = all.length - 1; i >= 2; i--) {
    const dropped = /** @type {keyof typeof BADGE_SR} */ (all[i].getAttribute('data-cfs-badge'));
    all[i].remove();
    const sr = card.querySelector('[data-cfs-sr-badges]');
    if (sr && BADGE_SR[dropped]) sr.textContent = (sr.textContent ?? '').replace(BADGE_SR[dropped], '');
  }
}

/**
 * Adds text to the card's screen reader badge line, creating it if needed.
 * @param {HTMLElement} card
 * @param {string} text
 */
function addBadgeSr(card, text) {
  const title = card.querySelector('.cfs-product-card__title');
  let sr = card.querySelector('[data-cfs-sr-badges]');
  if (!sr && title) {
    sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.setAttribute('data-cfs-sr-badges', '');
    title.append(sr);
  }
  if (sr && !(sr.textContent ?? '').includes(text)) sr.textContent = `${sr.textContent ?? ''}${text}`;
}

document.addEventListener(MULTIBUY_EVENT, () => {
  for (const card of document.querySelectorAll('cfs-product-card')) /** @type {any} */ (card).markMultibuy?.();
});
loadMultibuys();

class CfsProductCard extends HTMLElement {
  /** @type {number} quantity shown on the card */
  #shown = 0;
  /** @type {number} quantity the shopper has asked for */
  #wanted = 0;
  /** @type {number} quantity the cart is known to hold */
  #confirmed = 0;
  #timer = 0;
  #inFlight = false;
  #messageTimer = 0;

  get variantId() {
    return this.dataset.variantId ?? '';
  }

  get productTitle() {
    return this.dataset.productTitle ?? '';
  }

  // CFS (owner, 11 Oct 2026): the controls are looked up live, not kept from connect. Horizon's predictive
  // search morphs its results in place: the card element survives but its stepper could be swapped or
  // removed, and a card holding a detached stepper hid Add and showed nothing ("the stepper disappeared").
  get addButton() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-cfs-add]'));
  }
  get stepper() {
    return /** @type {HTMLElement} */ (this.querySelector('[data-cfs-stepper]') ?? this.#buildStepper());
  }
  get qtyEl() {
    return this.stepper.querySelector('[data-cfs-qty]');
  }
  get increaseButton() {
    return this.stepper.querySelector('[data-cfs-increase]');
  }
  get decreaseButton() {
    return this.stepper.querySelector('[data-cfs-decrease]');
  }
  get messageEl() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-cfs-message]'));
  }
  get preorderEl() {
    return this.querySelector('[data-cfs-preorder-note]');
  }

  /** @type {MutationObserver | null} */
  #observer = null;
  #bound = false;
  /** @type {WeakSet<HTMLInputElement>} */
  #boundFields = new WeakSet();

  connectedCallback() {
    this.markMultibuy();
    this.#markBoughtBefore();
    if (!this.addButton) return;

    if (!this.#bound) {
      this.#bound = true;
      // One delegated listener: keeps working when the buttons are replaced
      this.addEventListener('click', (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (target.closest('[data-cfs-add]')) this.#onAdd();
        else if (target.closest('[data-cfs-increase]')) this.#onIncrease();
        else if (target.closest('[data-cfs-decrease]')) this.#onDecrease();
      });
    }
    document.addEventListener(CART_STATE_EVENT, this.#onCartState);

    // Re-draw after the card's markup is replaced from outside (search results morph). The morph can also
    // reuse this element for another product (new data-variant-id), so re-read the basket for it.
    this.#observer ??= new MutationObserver(() => {
      if (!this.addButton) return;
      if (this.#inFlight || this.#timer) this.#render(this.#shown, false, false);
      else this.#syncFromCart(getCartState(), false);
    });
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-variant-id'] });

    this.#syncFromCart(getCartState(), false);
  }

  /** Typed quantities: bind each field once, including one that arrives in replaced markup. */
  #bindField() {
    const field = this.qtyEl;
    if (field instanceof HTMLInputElement && !this.#boundFields.has(field)) {
      this.#boundFields.add(field);
      bindQuantityField(field, (typed) => this.#onTyped(typed));
    }
  }

  #markBoughtBefore() {
    const badges = this.querySelector('[data-cfs-badges]');
    if (!badges || badges.querySelector('[data-cfs-badge="bought"]')) return;
    if (!boughtBeforeIds().has(this.dataset.productId ?? '')) return;

    const badge = document.createElement('span');
    badge.className = 'cfs-badge cfs-badge--bought';
    badge.dataset.cfsBadge = 'bought';
    badge.textContent = 'Bought before';
    const offers = badges.querySelectorAll('[data-cfs-badge="offer"], [data-cfs-badge="multibuy"], [data-cfs-badge="preorder"]');
    if (offers.length) offers[offers.length - 1].after(badge);
    else badges.prepend(badge);
    // Max two: drop the lowest-priority badges (New, Best seller come last).
    trimBadges(this);
    if (badge.isConnected) addBadgeSr(this, ', bought before');
  }

  /** Multibuy badge from Bundler's bundles (see loadMultibuys): "3 for £1.39", right after the Offer badge. */
  markMultibuy() {
    const label = multibuys.get(this.dataset.productId ?? '');
    const badges = this.querySelector('[data-cfs-badges]');
    if (!label || !badges) return;
    let badge = /** @type {HTMLElement | null} */ (badges.querySelector('[data-cfs-badge="multibuy"]'));
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'cfs-badge';
      badge.dataset.cfsBadge = 'multibuy';
      const offer = badges.querySelector('[data-cfs-badge="offer"]');
      if (offer) offer.after(badge);
      else badges.prepend(badge);
    }
    if (badge.textContent !== label) badge.textContent = label;
    trimBadges(this);
    if (badge.isConnected) addBadgeSr(this, `, multibuy offer ${label}`);
  }

  /** Builds the − qty + stepper next to the Add button (not server-rendered, to keep cards small). */
  #buildStepper() {
    const title = this.productTitle;
    const icon = (/** @type {string} */ name, /** @type {number} */ size) =>
      `<svg class="cfs-icon" aria-hidden="true" focusable="false" width="${size}" height="${size}"><use href="#cfs-i-${name}"/></svg>`;
    const stepper = document.createElement('div');
    stepper.className = 'cfs-stepper';
    stepper.setAttribute('role', 'group');
    stepper.setAttribute('aria-label', `${title} in basket`);
    stepper.dataset.cfsStepper = '';
    stepper.hidden = true;
    // Grid cards (owner, 10 Oct 2026): a white box between round − and + that reads as a field and takes a
    // typed quantity. Row cards too since 11 Oct 2026 (a compact size, assets/cfs-global.css).
    const field = true;
    stepper.classList.add('cfs-stepper--field');
    stepper.innerHTML =
      `<button type="button" class="cfs-stepper__button" data-cfs-decrease>${icon('minus', 16)}</button>` +
      (field
        ? '<input class="cfs-stepper__input" type="text" inputmode="numeric" pattern="[0-9]*" maxlength="3" autocomplete="off" value="0" data-cfs-qty>'
        : '<span class="cfs-stepper__qty" data-cfs-qty>0</span>') +
      `<button type="button" class="cfs-stepper__button" data-cfs-increase>${icon('plus', 18)}</button>`;
    if (field) /** @type {HTMLElement} */ (stepper.querySelector('[data-cfs-qty]')).setAttribute('aria-label', `Quantity of ${title} in basket`);
    /** @type {HTMLElement} */ (stepper.lastElementChild).setAttribute('aria-label', `Add one more ${title}`);
    /** @type {HTMLElement} */ (stepper.firstElementChild).setAttribute('aria-label', `Remove one ${title}`);
    this.addButton?.after(stepper);
    return stepper;
  }

  disconnectedCallback() {
    document.removeEventListener(CART_STATE_EVENT, this.#onCartState);
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    clearTimeout(this.#messageTimer);
  }

  #onCartState = () => {
    // Ignore cart broadcasts while this card has its own change pending.
    if (this.#inFlight || this.#timer) return;
    this.#syncFromCart(getCartState(), false);
  };

  /**
   * @param {import('./cfs-cart.js').CfsCart} _cart
   * @param {boolean} moveFocus
   */
  #syncFromCart(_cart, moveFocus) {
    const quantity = quantityForVariant(this.variantId);
    this.#confirmed = quantity;
    this.#wanted = quantity;
    this.#render(quantity, moveFocus, false);
  }

  /** Stock on hand of a tracked variant that stops selling at 0 (data-max), or Infinity. */
  get #max() {
    const max = this.dataset.max;
    return max === undefined || max === '' ? Infinity : Math.max(0, Number(max));
  }

  #onAdd = () => {
    if (this.#atMax(Math.max(1, this.#shown + 1))) return;
    this.#request(Math.max(1, this.#shown + 1), true);
  };
  #onIncrease = () => {
    if (this.#atMax(this.#shown + 1)) return;
    this.#request(this.#shown + 1, false);
  };

  /** A typed quantity: past the stock it stops at the stock and says so; 0 removes it. @param {number | null} typed */
  #onTyped(typed) {
    const max = this.#max;
    let next = typed ?? this.#shown;
    if (next > max) {
      next = max;
      this.#showMessage(max === 0 ? 'Sorry, this is sold out' : `Only ${max} available`);
    }
    if (next === this.#shown) {
      this.#render(this.#shown, false, false);
      return;
    }
    if (next < this.#shown) this.#setLimited(false);
    this.#request(next, next === 0);
  }

  /** Past the stock on hand: say so instead of asking the cart (owner, 10 Oct 2026). @param {number} next */
  #atMax(next) {
    const max = this.#max;
    if (next <= max) return false;
    this.#showMessage(max === 0 ? 'Sorry, this is sold out' : this.#shown >= max ? `All ${max} we have ${max === 1 ? 'is' : 'are'} in your basket` : `Only ${max} available`);
    return true;
  }
  #onDecrease = () => {
    this.#setLimited(false);
    this.#clearMessage();
    this.#request(Math.max(0, this.#shown - 1), this.#shown - 1 <= 0);
  };

  /**
   * @param {number} quantity
   * @param {boolean} moveFocus - Move focus when the control swaps (Add ↔ stepper).
   */
  #request(quantity, moveFocus) {
    this.#clearMessage();
    this.#wanted = quantity;
    this.#render(quantity, moveFocus);

    clearTimeout(this.#timer);
    // The first add goes straight away; stepper taps are coalesced into one request.
    const delay = this.#confirmed === 0 && quantity === 1 ? 0 : COALESCE_MS;
    this.#timer = window.setTimeout(() => {
      this.#timer = 0;
      this.#flush();
    }, delay);
  }

  async #flush() {
    if (this.#inFlight || this.#wanted === this.#confirmed) return;

    this.#inFlight = true;
    this.setAttribute('aria-busy', 'true');
    const target = this.#wanted;

    try {
      const cart = await setVariantTotal({ variantId: this.variantId, total: target, source: this });
      this.#confirmed = quantityForVariant(this.variantId, cart);
      if (this.#confirmed !== target && this.#wanted === target) {
        // The cart accepted fewer than asked (stock limit): show what is really in the basket.
        this.#wanted = this.#confirmed;
        this.#render(this.#confirmed, false, false);
        this.#showMessage(`Only ${this.#confirmed} available`);
      }
    } catch (error) {
      // Roll back to what the cart holds and explain, unless the shopper has tapped again since.
      this.#confirmed = quantityForVariant(this.variantId);
      if (this.#wanted === target) {
        this.#wanted = this.#confirmed;
        this.#render(this.#confirmed, document.activeElement?.closest('cfs-product-card') === this, false);
      }
      this.#showMessage(/** @type {Error} */ (error)?.message || 'Could not update your basket', Boolean(/** @type {any} */ (error)?.detail?.limit));
      if (/** @type {any} */ (error)?.detail?.limit) this.#setLimited(true);
    } finally {
      this.#inFlight = false;
      this.removeAttribute('aria-busy');
      // More taps arrived while the request was running.
      if (this.#wanted !== this.#confirmed && !this.#timer) this.#flush();
    }
  }

  /**
   * @param {number} quantity
   * @param {boolean} moveFocus
   * @param {boolean} [announce=true] - Announce the new quantity (only for the shopper's own taps).
   */
  #render(quantity, moveFocus, announce = true) {
    const wasStepper = this.#shown > 0;
    const isStepper = quantity > 0;
    this.#shown = quantity;
    this.#bindField();

    // CFS: write only what changed. Every card re-renders on each basket update, and each redundant
    // write is a DOM mutation that page-wide observers (apps, the drawer's "Little extras") react to.
    if (this.qtyEl instanceof HTMLInputElement) {
      // Not while the shopper is typing in it
      if (document.activeElement !== this.qtyEl && this.qtyEl.value !== String(quantity)) this.qtyEl.value = String(quantity);
    } else if (this.qtyEl && this.qtyEl.textContent !== String(quantity)) this.qtyEl.textContent = String(quantity);
    if (this.stepper.hidden !== !isStepper) this.stepper.hidden = !isStepper;
    if (this.addButton.hidden !== isStepper) this.addButton.hidden = isStepper;
    this.classList.toggle('cfs-product-card--in-basket', isStepper);

    // Pre-order products with stock on hand: say how many of the shopper's quantity are on pre-order.
    const preorder = this.preorderEl ? Math.max(0, quantity - (Number(this.dataset.stock) || 0)) : 0;
    if (this.preorderEl) {
      const note = preorder > 0 ? `${preorder} on pre-order` : '';
      if (this.preorderEl.textContent !== note) this.preorderEl.textContent = note;
    }

    // At the stock on hand: + looks greyed but still answers a tap with "All N we have…"
    if (this.increaseButton) {
      const atMax = quantity >= this.#max;
      if ((this.increaseButton.getAttribute('aria-disabled') === 'true') !== atMax) {
        if (atMax) this.increaseButton.setAttribute('aria-disabled', 'true');
        else this.increaseButton.removeAttribute('aria-disabled');
      }
    }

    if (this.decreaseButton) {
      const label = quantity === 1 ? `Remove ${this.productTitle} from basket` : `Remove one ${this.productTitle}`;
      if (this.decreaseButton.getAttribute('aria-label') !== label) this.decreaseButton.setAttribute('aria-label', label);
    }

    if (moveFocus && wasStepper !== isStepper) {
      (isStepper ? this.increaseButton : this.addButton)?.focus();
    }

    if (announce) {
      this.#announce(isStepper ? `${quantity} in basket${preorder ? `, ${preorder} on pre-order` : ''}` : 'Removed from basket');
    }
  }

  /** @param {string} text */
  #announce(text) {
    if (!this.messageEl || this.messageEl.dataset.error) return;
    this.messageEl.classList.add('visually-hidden');
    this.messageEl.textContent = text;
  }

  /**
   * @param {string} text
   * @param {boolean} [persistent] - Keep the message (e.g. a category limit) instead of fading it.
   */
  #showMessage(text, persistent = false) {
    if (!this.messageEl) return;
    clearTimeout(this.#messageTimer);
    this.messageEl.classList.remove('visually-hidden');
    this.messageEl.dataset.error = 'true';
    this.messageEl.textContent = text;
    if (!persistent) this.#messageTimer = window.setTimeout(() => this.#clearMessage(), MESSAGE_MS);
  }

  /** MinMaxify limit reached for this product's category: no more increases on this card. */
  /** @param {boolean} limited */
  #setLimited(limited) {
    this.classList.toggle('cfs-product-card--limited', limited);
    if (this.increaseButton instanceof HTMLButtonElement) this.increaseButton.disabled = limited;
  }

  #clearMessage() {
    if (!this.messageEl) return;
    delete this.messageEl.dataset.error;
    this.messageEl.textContent = '';
  }
}

if (!customElements.get('cfs-product-card')) customElements.define('cfs-product-card', CfsProductCard);
